import { batteryProductShot } from "../../ProposalPresentation/battery-product-shot"
import type { BatteryPresentationProposal } from "../presentation-proposal"
import {
  Eyebrow,
  GradText,
  Icon,
  Panel,
  Section,
  SectionHeading,
} from "../primitives"
import { useDesktopViewport } from "../use-desktop-viewport"

export function Specs({
  proposal,
}: {
  proposal: BatteryPresentationProposal
}): JSX.Element {
  const isDesktop = useDesktopViewport()
  const { equipment } = proposal
  const model = [equipment?.manufacturer, equipment?.name]
    .filter(Boolean)
    .join(` `)
  const productShot = batteryProductShot(
    equipment?.manufacturer,
    equipment?.name,
  )
  const rows: Array<[string, string]> = []
  if (equipment?.usableKwh != null) {
    rows.push([`Usable capacity`, `${equipment.usableKwh} kWh`])
  }
  if (equipment?.ratedOutputKw != null) {
    rows.push([`Continuous power`, `${equipment.ratedOutputKw} kW`])
  }
  if (equipment?.roundTripEfficiency != null) {
    const percent =
      equipment.roundTripEfficiency > 1
        ? equipment.roundTripEfficiency
        : equipment.roundTripEfficiency * 100
    rows.push([`Round-trip efficiency`, `${percent}%`])
  }
  if (equipment != null) {
    rows.push([
      `Manufacturer Warranty`,
      equipment.manufacturerWarrantyLengthYears != null
        ? `${equipment.manufacturerWarrantyLengthYears} years`
        : `—`,
    ])
    rows.push([
      `Load Start Capability (LRA)`,
      equipment.loadStartCapabilityLRA != null
        ? String(equipment.loadStartCapabilityLRA)
        : `—`,
    ])
  }
  if (equipment?.quantity != null) {
    rows.push([`Units`, String(equipment.quantity)])
  }

  return (
    <Section id="specs">
      <SectionHeading
        eyebrow="Your energy insurance policy"
        sub="Seamless backup power with rate-shift energy management, all managed automatically."
      >
        Meet{` `}
        {model ? (
          <GradText>{model}</GradText>
        ) : (
          <>
            your <GradText>home battery</GradText>
          </>
        )}
        .
      </SectionHeading>
      {isDesktop ? (
        <div className="grid items-stretch gap-3.5 lg:grid-cols-2">
          <Panel className="px-9 py-8">
            <Eyebrow className="mb-4">System specifications</Eyebrow>
            {rows.length > 0 ? (
              rows.map(([label, value], index) => (
                <div
                  key={label}
                  className={`flex items-baseline justify-between gap-4 py-3.5 ${
                    index < rows.length - 1 ? `border-b border-bp-border` : ``
                  }`}
                >
                  <span className="text-[length:var(--step--1)] text-bp-text-muted">
                    {label}
                  </span>
                  <span className="font-display text-right text-[length:var(--step-0)] font-semibold tabular-nums text-bp-text">
                    {value}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-[length:var(--step--1)] text-bp-text-muted">
                Equipment specifications appear when a battery snapshot is
                stored.
              </p>
            )}
          </Panel>
          <div className="relative flex min-h-[240px] flex-col justify-center overflow-hidden rounded-lg border border-bp-border bg-bp-elevated px-8 py-6">
            <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-product-glow)]" />
            {productShot ? (
              <img
                src={productShot.src}
                alt={productShot.alt}
                loading="lazy"
                decoding="async"
                className="relative mx-auto max-h-[230px] w-[82%] object-contain"
              />
            ) : (
              <div className="relative mx-auto grid size-[180px] place-items-center rounded-full border border-bp-border-strong bg-bp-glass-hi text-bp-accent">
                <Icon name="battery" className="size-20" />
              </div>
            )}
            <div className="relative mt-2 flex flex-wrap items-baseline justify-between gap-2 border-t border-bp-border pt-3">
              <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text-soft">
                {model || `Home battery system`}
              </span>
              {equipment?.usableKwh != null ? (
                <span className="text-[length:var(--step--2)] text-bp-text-muted">
                  {equipment.usableKwh} kWh usable
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <Panel className="px-5 py-2">
          <div className="flex items-center justify-between gap-4 border-b border-bp-border py-3">
            <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text-soft">
              {model || `Home battery system`}
            </span>
            {productShot ? (
              <img
                src={productShot.src}
                alt={productShot.alt}
                loading="lazy"
                decoding="async"
                className="size-16 object-contain"
              />
            ) : (
              <div className="grid size-12 place-items-center rounded-full bg-bp-glass-hi text-bp-accent">
                <Icon name="battery" className="size-6" />
              </div>
            )}
          </div>
          {rows.length > 0 ? (
            rows.map(([label, value], index) => (
              <div
                key={label}
                className={`flex items-baseline justify-between gap-4 py-2.5 ${
                  index < rows.length - 1 ? `border-b border-bp-border` : ``
                }`}
              >
                <span className="text-[length:var(--step--1)] text-bp-text-muted">
                  {label}
                </span>
                <span className="font-display text-right text-[length:var(--step-0)] font-semibold tabular-nums text-bp-text">
                  {value}
                </span>
              </div>
            ))
          ) : (
            <p className="py-3 text-[length:var(--step--1)] text-bp-text-muted">
              Equipment specifications appear when a battery snapshot is stored.
            </p>
          )}
        </Panel>
      )}
    </Section>
  )
}
